// M4-A §2 Completion design & the frac job, DUCs and refracs. A drilled lateral (AT_TD, well.duc) is completed by a
// frac job (well.frac, status COMPLETING) that runs daily in tickPre: queued (needs a contracted fleet, proppant in
// stock, frac water, no rig on the pad unless SIMOPS) → pumping (stages/day × zipper × sleeves, offsets shut in) →
// drill-out (plug-and-perf) → flowback → PRODUCING. Waits are well.problem NO_FLEET / NO_SAND / NO_WATER / RIG_ON_PAD.
import { COMPLETION, DUC, PAD, shaleIp30, modHypEur, M4_FROM } from '../../data/m4/rules.js';
import { eqDef, fieldDef, stat, yf, isAvailable, unitPrice, eraDollars, defsByCat } from '../defs.js';
import { chance, range, int } from '../../core/rng.js';
import { stockQty, takeStock, addStock } from '../../core/state.js';
import { setProblem, wellRig } from '../drilling.js';
import { notify, spend, fmt$, log, clamp } from '../util.js';
import { SHALE_SIM, sh, tierOf, tierMult, completionSkillMult, resDefOf, eff, upg, ok, no, round } from './shaleUtil.js';
import { childInfo, fracHits, shutInOffsets, rigOnPad, simopsAllowed } from './shalePads.js';
import { serviceDayRate, fracServices, activeService, contractFracServiceImpl, releaseFracServiceImpl, sandDeliveredPerTon, defaultProppant, inBasinAvailable,
  sandShortageChance, fracWaterSupply, fracIndexNow } from './shaleServices.js';
import { startShaleProduction } from './shaleProd.js';
import { expectedInZone } from './shaleLaterals.js';
import { bestCement, cementPrice } from '../cement.js';

// ---- design options ---------------------------------------------------------------------------------------------
const OPEN_HOLE = { id: 'openhole', name: 'Open-Hole Single-Point Frac', stats: { stageCost: 40000, baseYear: 2000, isolation: 'openhole', minSpacingFt: 1500, qiMultRange: [0.85, 0.85], needsWireline: false, needsDrillout: false } };
export function isolationOptions(state) {
  const out = ['dissolvable_plugs', 'plug_and_perf', 'sliding_sleeve'].map(eqDef).filter((d) => d && isAvailable(d, state));
  return out.length ? out : [OPEN_HOLE];
}
export const isolationDef = (id) => (id === 'openhole' ? OPEN_HOLE : eqDef(id));
export function fluidOptions(state) {
  return Object.keys(COMPLETION.fluids).filter((f) => f !== 'slickwater' || upg(state, 'slickwater_fracs'));
}
export function designLimits(state) {
  return { ppf: [200, 2000 + (eff(state).maxPpfAdd ?? 0)], spacingFt: [100, 1000], fluids: fluidOptions(state), isolation: isolationOptions(state).map((d) => d.id) };
}
const stepVal = (keys, y) => { let v = keys[0][1]; for (const [yy, x] of keys) if (y >= yy) v = x; return v; };
// What an average operator pumped that year (COMPLETION.typicalByYear), with the best tools on the market.
export function typicalDesign(state, well) {
  const t = stepVal(COMPLETION.typicalByYear, yf(state));
  const lim = designLimits(state);
  const iso = isolationOptions(state);
  const pref = iso.find((d) => d.id === 'plug_and_perf') ?? iso[0];
  return { proppantLbPerFt: clamp(t.ppf, ...lim.ppf), stageSpacingFt: t.spacingFt, fluid: lim.fluids.includes('slickwater') ? 'slickwater' : lim.fluids[0], isolation: pref.id, proppant: defaultProppant(state, well.fieldId) };
}
export function normalizeDesign(state, well, d = {}) {
  const base = { ...typicalDesign(state, well), ...(well.lateral?.design ?? {}), ...(d ?? {}) };
  const lim = designLimits(state);
  const iso = isolationOptions(state).some((x) => x.id === base.isolation) ? base.isolation : typicalDesign(state, well).isolation;
  const fluid = lim.fluids.includes(base.fluid) ? base.fluid : lim.fluids[0];
  const prop = eqDef(base.proppant) && isAvailable(eqDef(base.proppant), state) && (!stat(eqDef(base.proppant), 'inBasin', false) || inBasinAvailable(state, well.fieldId)) ? base.proppant : defaultProppant(state, well.fieldId);
  return { proppantLbPerFt: Math.round(clamp(+base.proppantLbPerFt || 1500, ...lim.ppf)), stageSpacingFt: Math.round(clamp(+base.stageSpacingFt || 200, ...lim.spacingFt)), fluid, isolation: iso, proppant: prop };
}
export function setCompletionDesignImpl(state, well, d) {
  if (!well?.lateral) return no('Completion designs are for horizontal shale wells.');
  if (well.frac && well.frac.phase !== 'queued') return no('The frac is already being pumped.');
  well.lateral.design = normalizeDesign(state, well, d);
  if (well.frac?.phase === 'queued') Object.assign(well.frac, jobShape(state, well, well.lateral.design));
  const p = projectCompletion(state, well);
  return ok(`${well.name}: ${p.stages} stages, ${well.lateral.design.proppantLbPerFt.toLocaleString('en-US')} lb/ft — projected IP30 ${Math.round(p.ip30)} boe/d for ~${fmt$(p.cost.total)}.`, { projection: p });
}

// ---- fleets ---------------------------------------------------------------------------------------------------
export function bestFleetDef(state, fid) {
  const list = defsByCat('completion').filter((d) => stat(d, 'service', null) === 'frac' && isAvailable(d, state) && !stat(d, 'minPadWells', 0) && (!stat(d, 'needsTurbinePower', false)));
  list.sort((a, b) => stat(b, 'stagesPerDay', 4) / serviceDayRate(state, b) - stat(a, 'stagesPerDay', 4) / serviceDayRate(state, a));
  return list[0] ?? null;
}
const fleetsAt = (state, fid) => fracServices(state, fid, 'frac');

// ---- projection (UI & the job) -----------------------------------------------------------------------------------
function jobShape(state, well, design) {
  const L = well.lateral;
  const lat = L.lengthFt > 0 ? L.lengthFt : L.targetFt;
  const iso = isolationDef(design.isolation);
  const minSp = stat(iso, 'minSpacingFt', 0);
  let stages = Math.max(1, Math.ceil(lat / Math.max(design.stageSpacingFt, minSp)));
  const maxSt = stat(iso, 'maxStages', null);
  if (maxSt) stages = Math.min(stages, maxSt);
  const sandTons = lat * design.proppantLbPerFt * COMPLETION.sandTonsPerLb;
  const waterBbl = lat * (COMPLETION.fluids[design.fluid]?.waterBblPerFt ?? 40);
  return { lateralFt: lat, stages, spacingEffFt: round(lat / stages, 1), sandTons: round(sandTons, 1), waterBbl: Math.round(waterBbl), waterPerStage: waterBbl / stages };
}
export function ip30For(state, well, design, { childMult = null, inZone = null } = {}) {
  const L = well.lateral;
  const r = resDefOf(well.fieldId, L.bench);
  if (!r?.typeCurve) return 0;
  const shape = jobShape(state, well, design);
  const plot = state.fields[well.fieldId].plots[well.plot];
  const iz = inZone ?? L.inZoneFrac ?? expectedInZone(state, wellRig(state, well), L.windowFt);
  const cm = childMult ?? childInfo(state, well).mult;
  const capHi = 1.3 + (eff(state).proppantCapAdd ?? 0);
  const raw = Math.pow(design.proppantLbPerFt / COMPLETION.reference.proppantLbPerFt, COMPLETION.proppantExp);
  const hi = raw > 1.3 ? Math.min(raw, capHi) / 1.3 : 1;
  const iso = isolationDef(design.isolation);
  return shaleIp30(r.typeCurve, { lateralFt: shape.lateralFt, proppantLbPerFt: design.proppantLbPerFt, stageSpacingFt: shape.spacingEffFt, fluid: design.fluid,
    tierMult: tierMult(tierOf(state, well.fieldId, well.plot)), inZoneFrac: iz, childMult: cm, roll: plot.truth?.qiRoll ?? 0.5 })
    * hi * completionSkillMult(state) * (stat(iso, 'qiMultRange', [1, 1])[0]) * stat(eqDef(design.proppant), 'meshMult', 1);
}
const ducAge = (state, well) => (well.duc ? state.day - well.duc.since : 0);
export function ducReworkMult(age) {
  return 1 + DUC.reworkPerMonth * Math.max(0, age - DUC.graceDays) / 30.44;
}
// M4 balance: a lateral is fracked through a cemented frac-rated production string run to the toe, plus its share
// of surface & intermediate pipe (COMPLETION.prodCasing). Bot-drilled and player laterals reached TD open-hole, so
// the string is run (and charged) when the frac is scheduled — unless the hole is already cased to the landing TVD.
export function prodCasingNeed(state, well) {
  const L = well?.lateral;
  if (!L || L.prodCasing || well.prod) return null;
  if ((well.casing ?? []).some((c) => c.bottom >= (L.landingTvd ?? well.depth) - 1)) return null;
  const P = COMPLETION.prodCasing;
  const md = well.md ?? well.depth;
  const ft = Math.round(md + P.upperStringsShareOfTvd * (L.landingTvd ?? well.depth));
  const perFt = eraDollars(state, P.pricePerFt, P.baseYear);
  const cem = bestCement(state);
  return { ft, perFt, cost: Math.round(ft * perFt + cementPrice(state, cem) * P.cementJobs), cementId: cem?.id ?? null };
}
// Wellhead, tubing, flowlines and the well's share of the tank battery (COMPLETION.tieIn), once per lateral.
export const tieInCost = (state, well) => (well?.lateral && !well.lateral.tieIn && !well.prod ? Math.round(eraDollars(state, COMPLETION.tieIn.cost, COMPLETION.tieIn.baseYear)) : 0);
function runProdCasing(state, well) {
  const need = prodCasingNeed(state, well);
  const tie = tieInCost(state, well);
  const cost = (need?.cost ?? 0) + tie;
  if (!cost) return 0;
  spend(state, cost, 'consumables', false);
  well.cost += cost;
  if (need) well.lateral.prodCasing = { ft: need.ft, cost: need.cost, day: state.day };
  if (tie) well.lateral.tieIn = { cost: tie, day: state.day };
  return cost;
}
// Projected IP30, EUR, days and cost of a design (default: the well's design or the year's typical one).
export function projectCompletion(state, well, design = null, { fleetDefId = null, zipper = null } = {}) {
  if (!well?.lateral) return null;
  const d = normalizeDesign(state, well, design ?? {});
  const fid = well.fieldId;
  const shape = jobShape(state, well, d);
  const iso = isolationDef(d.isolation);
  const contracted = fleetsAt(state, fid)[0];
  const fleet = eqDef(fleetDefId) ?? (contracted ? eqDef(contracted.defId) : bestFleetDef(state, fid));
  const zip = zipper ?? (!!eff(state).zipperFrac && yf(state) >= PAD.zipperFrac.from);
  // one well on its own; a zipper pair on the pad pumps 1.6× the stages per fleet-day (shared between the two wells)
  const spd = Math.max(0.5, stat(fleet, 'stagesPerDay', COMPLETION.stagesPerDayBase) * stat(iso, 'stagesPerDayMult', 1));
  const pumpDays = shape.stages / spd;
  const ct = eqDef('coiled_tubing_unit'), fb = eqDef('flowback_crew');
  const drilloutDays = stat(iso, 'needsDrillout', false) ? Math.ceil(shape.stages / stat(ct, 'plugsPerDay', COMPLETION.plugDrilloutPerDay)) : 0;
  const flowbackDays = stat(fb, 'days', SHALE_SIM.flowbackDays);
  const supply = fracWaterSupply(state, fid);
  const chem = eraDollars(state, COMPLETION.fluids[d.fluid]?.chemPerBbl ?? 0.4, COMPLETION.baseYear);
  const stageUnit = (iso === OPEN_HOLE ? eraDollars(state, OPEN_HOLE.stats.stageCost, OPEN_HOLE.stats.baseYear) : unitPrice(state, iso)) * stageCostMult(state);
  const age = ducAge(state, well);
  const cost = {
    stages: shape.stages * stageUnit,
    wireline: stat(iso, 'needsWireline', false) ? shape.stages * eraDollars(state, COMPLETION.wirelinePerStage, COMPLETION.baseYear) * (wirelineDiscount(state)) * stageCostMult(state) : 0,
    fleet: fleet ? Math.ceil(pumpDays) * serviceDayRate(state, fleet) : 0,
    sand: shape.sandTons * sandDeliveredPerTon(state, d.proppant, fid),
    water: shape.waterBbl * ((Number.isFinite(supply.costPerBbl) ? supply.costPerBbl : 0) + chem),
    drillout: drilloutDays * serviceDayRate(state, ct),
    flowback: flowbackDays * serviceDayRate(state, fb),
    fuel: fleet ? Math.ceil(pumpDays) * eraDollars(state, SHALE_SIM.fracFuelPerDay, SHALE_SIM.fracFuelBaseYear) * stat(fleet, 'fuelMult', 1) : 0,
    casing: prodCasingNeed(state, well)?.cost ?? 0, // M4 balance: production string (charged when the frac is scheduled)
    tieIn: tieInCost(state, well),                   // M4 balance: wellhead, tubing, flowlines, battery share
  };
  const base = Object.values(cost).reduce((a, b) => a + b, 0);
  cost.ducRework = base * (ducReworkMult(age) - 1) + (age > DUC.maxAgeDays ? eraDollars(state, SHALE_SIM.ducReinspectCost, SHALE_SIM.ducBaseYear) : 0);
  cost.total = base + cost.ducRework;
  for (const k of Object.keys(cost)) cost[k] = Math.round(cost[k]);
  const ch = childInfo(state, well);
  const ip30 = ip30For(state, well, d, { childMult: ch.mult });
  const r = resDefOf(fid, well.lateral.bench);
  const tc = r?.typeCurve;
  const eur = tc ? modHypEur(ip30, r.di, tc.b, tc.dmin ?? COMPLETION.dminDefault) : 0;
  return {
    design: d, ...shape, stagesPerDay: round(spd, 2), pumpDays: round(pumpDays, 1), drilloutDays, flowbackDays, reinspectDays: age > DUC.maxAgeDays ? SHALE_SIM.ducReinspectDays : 0,
    totalDays: Math.ceil(pumpDays) + drilloutDays + flowbackDays + (age > DUC.maxAgeDays ? SHALE_SIM.ducReinspectDays : 0),
    zipperEligible: zip, zipperPairDays: zip ? round(2 * shape.stages / (spd * PAD.zipperFrac.stagesPerDayMult), 1) : null,
    fleetDefId: fleet?.id ?? null, fracRateIndex: round(fracIndexNow(state), 3), cost, ip30: round(ip30, 1), eur: Math.round(eur), eurPerIp30Days: ip30 > 0 ? Math.round(eur / ip30) : null,
    costPerBoe: eur > 0 ? round(cost.total / eur, 2) : null, child: ch, tier: tierOf(state, fid, well.plot), ducAgeDays: age, ducRework: ducReworkMult(age) > 1,
    waterSpot: !!supply.spot, waterCapacity: Number.isFinite(supply.capacity) ? supply.capacity : null,
  };
}
// M4 balance: per-stage service pricing (plugs, perforating, pumping charges) follows the pressure-pumping market like
// the fleet day rate — 2014's tight market adds ~12%, the 2016 bust takes ~35% off (COMPLETION.stageCostIndexExp).
export const stageCostMult = (state) => Math.pow(Math.max(0.1, fracIndexNow(state)), COMPLETION.stageCostIndexExp ?? 0);
const wirelineDiscount = (state) => (Object.values(state.crew).some((c) => c.role === 'wireline_operator' && c.status === 'active') ? 1 - SHALE_SIM.wirelineOpDiscount : 1);

// ---- the job ----------------------------------------------------------------------------------------------------
export function fracWellImpl(state, well, design = null) {
  if (yf(state) < M4_FROM) return no('Multi-stage fracs are a shale-era job.');
  if (!well?.lateral) return no('Not a horizontal shale well.');
  if (well.frac) return no(`${well.name} is already on the frac schedule.`);
  if (well.lateral.phase !== 'done' || well.status !== 'AT_TD') return no('Finish drilling the lateral first.');
  const d = normalizeDesign(state, well, design ?? {});
  well.lateral.design = d;
  const S = sh(state);
  well.frac = { id: S.nextJob++, kind: 'frac', phase: 'queued', design: d, ...jobShape(state, well, d), stagesDone: 0, queuedDay: state.day, startDay: null,
    wait: null, fleetUid: null, sandPaid: false, shortageRolled: false, cost: 0, ip30: null, drilloutLeft: 0, flowbackLeft: 0, prepLeft: 0, reinspected: false };
  well.status = 'COMPLETING';
  well.problem = null;
  const casing = runProdCasing(state, well); // M4 balance
  autoBook(state, well.fieldId);
  return ok(`${well.name}: ${casing ? `production casing, wellhead & tie-in (${fmt$(casing)}); ` : ''}frac scheduled — ${well.frac.stages} stages, ${Math.round(well.frac.sandTons).toLocaleString('en-US')} t of sand, ${well.frac.waterBbl.toLocaleString('en-US')} bbl of water.`);
}
// Put a queued job back in the DUC inventory.
export function deferFracImpl(state, well) {
  if (!well?.frac) return no('Nothing scheduled.');
  if (well.frac.phase !== 'queued') return no('The job is already under way.');
  if (well.frac.kind === 'refrac') { well.status = 'PRODUCING'; } else well.status = 'AT_TD';
  well.frac = null; well.problem = null;
  return ok(`${well.name} left as a DUC.`);
}
export function refracImpl(state, well) {
  if (!well?.prod?.shale || !well.lateral) return no('Refracs are for producing shale wells.');
  if (well.frac) return no('Already on the frac schedule.');
  const def = eqDef('refrac_job');
  if (!def || !isAvailable(def, state) || !upg(state, 'refracturing')) return no('Diverter refracs come with the Refracturing research (2016).');
  if (!['PRODUCING', 'SHUT_IN', 'DEPLETED'].includes(well.status)) return no(`Can't refrac while ${well.status.toLowerCase().replace(/_/g, ' ')}.`);
  if ((well.prod.age ?? 0) < 730) return no('Wait until the well is at least two years old.');
  const S = sh(state);
  well.frac = { id: S.nextJob++, kind: 'refrac', phase: 'queued', design: null, stages: 1, stagesDone: 0, lateralFt: well.lateral.lengthFt, sandTons: 0, waterBbl: 0, waterPerStage: 0,
    queuedDay: state.day, startDay: null, wait: null, fleetUid: null, sandPaid: true, shortageRolled: true, cost: 0, pumpLeft: SHALE_SIM.refracFleetDays, drilloutLeft: 0, flowbackLeft: 0, prepLeft: 0, reinspected: true, prevStatus: well.status };
  well.status = 'COMPLETING';
  well.prod.rate = 0;
  autoBook(state, well.fieldId);
  return ok(`${well.name}: refrac scheduled (${fmt$(unitPrice(state, def))} + ${SHALE_SIM.refracFleetDays} fleet days).`);
}

function autoBook(state, fid) {
  if (!(state.shale?.auto?.frac ?? true) || fleetsAt(state, fid).length) return;
  const d = bestFleetDef(state, fid);
  if (!d) return;
  const r = contractFracServiceImpl(state, d.id, { fieldId: fid, auto: true });
  if (r.ok) log(state, `Booked a ${d.name} for ${fieldDef(fid)?.name}: ${r.msg}`);
}
const charge = (state, well, amt, cat = 'consumables') => { if (amt > 0) { spend(state, amt, cat, false); well.cost += amt; well.frac.cost += amt; } };
function wait(state, well, reason, text) {
  const j = well.frac;
  if (j.wait?.reason !== reason) j.wait = { reason, since: state.day };
  setProblem(state, well, reason, text);
}

// Daily (tickPre): advance every job. Pumping jobs claim their fleets first, then queued ones try to start.
export function tickFrac(state) {
  const S = state.shale;
  if (!S) return;
  const jobs = [];
  for (const w of Object.values(state.wells)) if (w.frac) jobs.push(w);
  if (!jobs.length) return;
  jobs.sort((a, b) => (a.frac.phase === 'pumping' ? 0 : 1) - (b.frac.phase === 'pumping' ? 0 : 1) || a.frac.id - b.frac.id);
  const load = {}; // fleet uid → wells pumping today
  for (const w of jobs) if (w.frac.phase === 'pumping' && w.frac.fleetUid) (load[w.frac.fleetUid] ??= []).push(w);
  for (const w of jobs) {
    const j = w.frac;
    if (j.phase === 'queued') tryStart(state, w, load);
    else if (j.phase === 'prep') { if (--j.prepLeft <= 0) j.phase = 'queued'; }
    else if (j.phase === 'drillout') { charge(state, w, ctRate(state, w)); if (--j.drilloutLeft <= 0) toFlowback(state, w); }
    else if (j.phase === 'flowback') { charge(state, w, fbRate(state, w)); if (--j.flowbackLeft <= 0) finishJob(state, w); }
  }
  for (const [uid, ws] of Object.entries(load)) for (const w of ws) if (w.frac?.phase === 'pumping') pumpDay(state, w, uid, ws.length);
  // auto fleets go home when the field has nothing left to pump
  for (const c of Object.values(S.services)) {
    if (!c.auto || c.kind !== 'frac') continue;
    if (!jobs.some((w) => w.fieldId === c.fieldId && w.frac && (w.frac.phase === 'queued' || w.frac.phase === 'pumping' || w.frac.phase === 'prep'))) releaseFracServiceImpl(state, c.uid);
  }
}
const ctRate = (state, w) => { const c = fracServices(state, w.fieldId, 'coiled_tubing').find((x) => activeService(state, x)); return c ? 0 : serviceDayRate(state, eqDef('coiled_tubing_unit')); };
const fbRate = (state, w) => { const c = fracServices(state, w.fieldId, 'flowback').find((x) => activeService(state, x)); return c ? 0 : serviceDayRate(state, eqDef('flowback_crew')); };
function fleetCapacity(state, def, w, mates) {
  if (stat(def, 'wellsAtOnce', 1) >= 2) return 2;
  if (eff(state).zipperFrac && yf(state) >= PAD.zipperFrac.from && mates.every((m) => m.lateral?.padUid === w.lateral?.padUid)) return 2;
  return 1;
}

function tryStart(state, w, load) {
  const j = w.frac;
  const fid = w.fieldId;
  // 1. a frac fleet on location with room
  const fleets = fleetsAt(state, fid);
  const live = fleets.filter((c) => activeService(state, c));
  let fleet = null;
  for (const c of live) { const mates = load[c.uid] ?? []; if (mates.length < fleetCapacity(state, eqDef(c.defId), w, mates)) { fleet = c; break; } }
  if (!fleet) {
    if (!fleets.length) autoBook(state, fid);
    const booked = fracServices(state, fid, 'frac').find((c) => !activeService(state, c));
    if (booked) return wait(state, w, 'NO_FLEET', `waiting for the frac fleet — on location in ${booked.startDay - state.day} days.`);
    if (live.length) return wait(state, w, 'NO_FLEET', 'the frac fleet is busy on another well.');
    return wait(state, w, 'NO_FLEET', 'no frac fleet contracted for this field — contract one (Shop → Completion).');
  }
  // 2. SIMOPS: no fracs next to a working rig without procedures
  if (rigOnPad(state, w) && !simopsAllowed(state)) return wait(state, w, 'RIG_ON_PAD', 'a rig is drilling on the pad — without SIMOPS procedures the frac waits for it to leave.');
  // 3. sand
  if (!j.sandPaid) {
    if (!j.shortageRolled) { j.shortageRolled = true; if (chance(state, sandShortageChance(state))) j.sandUntil = state.day + int(state, 3, 10); }
    if ((j.sandUntil ?? 0) > state.day) return wait(state, w, 'NO_SAND', `the sand mines can't keep up — proppant on location in ${j.sandUntil - state.day} days.`);
    const pid = j.design.proppant;
    const have = stockQty(state, pid);
    if (have < j.sandTons - 1e-6) {
      const short = j.sandTons - have;
      const price = sandDeliveredPerTon(state, pid, fid);
      if (!(state.shale.auto?.sand ?? true)) return wait(state, w, 'NO_SAND', `needs ${Math.round(j.sandTons).toLocaleString('en-US')} t of ${eqDef(pid)?.name} in stock (have ${Math.round(have).toLocaleString('en-US')}).`);
      if (state.company.cash < short * price) return wait(state, w, 'NO_SAND', `can't pay for ${Math.round(short).toLocaleString('en-US')} t of sand (${fmt$(short * price)}).`);
      spend(state, short * price, 'consumables');
      addStock(state, pid, short);
    }
    // sand in stock was bought at the shop: charge its delivered price into the well cost for the record
    takeStock(state, pid, j.sandTons);
    const sandCost = j.sandTons * sandDeliveredPerTon(state, pid, fid);
    w.cost += sandCost; j.cost += sandCost;
    j.sandPaid = true;
  }
  // 4. water
  const sup = fracWaterSupply(state, fid);
  if (j.kind === 'frac' && !(sup.capacity > 0)) return wait(state, w, 'NO_WATER', 'no frac water — contract a source (fresh water, brackish well or recycling).');
  // 5. old DUCs: casing inspection first
  const age = ducAge(state, w);
  if (j.kind === 'frac' && age > DUC.maxAgeDays && !j.reinspected) {
    j.reinspected = true; j.phase = 'prep'; j.prepLeft = SHALE_SIM.ducReinspectDays;
    charge(state, w, eraDollars(state, SHALE_SIM.ducReinspectCost, SHALE_SIM.ducBaseYear));
    w.problem = null;
    return log(state, `${w.name}: five years as a DUC — re-inspecting the casing before the frac (${SHALE_SIM.ducReinspectDays} days).`, 'info', { wellId: w.id });
  }
  // start pumping
  j.phase = 'pumping'; j.startDay = state.day; j.fleetUid = fleet.uid; j.wait = null; w.problem = null;
  (load[fleet.uid] ??= []).push(w);
  if (j.kind === 'refrac') { charge(state, w, unitPrice(state, eqDef('refrac_job'))); return; }
  const ch = childInfo(state, w);
  j.child = ch;
  if (ch.child && !w.lateral.childOf.includes(ch.parentId)) w.lateral.childOf.push(ch.parentId);
  j.ip30 = round(ip30For(state, w, j.design, { childMult: ch.mult }), 1);
  j.hits = fracHits(state, w);
  if (age > DUC.graceDays) { const p = projectCompletion(state, w, j.design); charge(state, w, p.cost.ducRework - (age > DUC.maxAgeDays ? eraDollars(state, SHALE_SIM.ducReinspectCost, SHALE_SIM.ducBaseYear) : 0)); }
  notify(state, 'frac', { wellId: w.id, fieldId: fid, stages: j.stages, text: `${w.name}: frac crew pumping ${j.stages} stages${ch.child ? ` (a child well ${ch.distFt} ft from ${state.wells[ch.parentId]?.name}: −${Math.round(ch.penalty * 100)}%)` : ''}.` });
  if (j.hits.length) notify(state, 'problem', { wellId: w.id, fieldId: fid, problem: 'FRAC_HIT', level: 'warn', text: `${w.name}'s frac hit ${j.hits.map((id) => state.wells[id]?.name).join(', ')} — the parent${j.hits.length > 1 ? 's lose' : ' loses'} 15% for two months and 5% for good.` });
}

function pumpDay(state, w, fleetUid, sharing) {
  const j = w.frac;
  const c = state.shale.services[fleetUid];
  if (!c || !activeService(state, c)) { j.phase = 'queued'; j.fleetUid = null; return; }
  if (rigOnPad(state, w) && !simopsAllowed(state)) return wait(state, w, 'RIG_ON_PAD', 'a rig moved onto the pad — pumping paused until it leaves (no SIMOPS).');
  c.busyDay = state.day;
  w.problem = null;
  shutInOffsets(state, w);
  if (j.kind === 'refrac') {
    if (--j.pumpLeft <= 0) { j.flowbackLeft = SHALE_SIM.refracFlowbackDays; j.phase = 'flowback'; }
    return;
  }
  const def = eqDef(c.defId);
  const iso = isolationDef(j.design.isolation);
  const zip = sharing >= 2 && stat(def, 'wellsAtOnce', 1) < 2 ? PAD.zipperFrac.stagesPerDayMult : 1;
  let st = stat(def, 'stagesPerDay', COMPLETION.stagesPerDayBase) * stat(iso, 'stagesPerDayMult', 1) * zip / sharing;
  const sup = fracWaterSupply(state, w.fieldId);
  const fs = state.shale.fields[w.fieldId];
  if (fs) { if (fs.fracDay !== state.day) { fs.fracDay = state.day; fs.fracWaterToday = 0; fs.fracCo2T = 0; fs.fracFuel = 0; } }
  const used = fs?.fracDay === state.day ? fs.fracWaterToday : 0;
  if (Number.isFinite(sup.capacity)) st = Math.min(st, Math.max(0, sup.capacity - used) / Math.max(1, j.waterPerStage));
  st = Math.min(st, j.stages - j.stagesDone);
  if (st <= 1e-6) return wait(state, w, 'NO_WATER', 'not enough frac water today — the source is tapped out.');
  j.stagesDone = round(j.stagesDone + st, 3);
  const water = st * j.waterPerStage;
  if (fs) fs.fracWaterToday = (fs.fracWaterToday ?? 0) + water;
  const sm = stageCostMult(state);
  const stageUnit = (iso.id === 'openhole' ? eraDollars(state, iso.stats.stageCost, iso.stats.baseYear) : unitPrice(state, iso)) * sm;
  const wl = stat(iso, 'needsWireline', false) ? eraDollars(state, COMPLETION.wirelinePerStage, COMPLETION.baseYear) * wirelineDiscount(state) * sm : 0;
  charge(state, w, st * (stageUnit + wl) + water * ((Number.isFinite(sup.costPerBbl) ? sup.costPerBbl : 0) + eraDollars(state, COMPLETION.fluids[j.design.fluid]?.chemPerBbl ?? 0.4, COMPLETION.baseYear)));
  const fuel = eraDollars(state, SHALE_SIM.fracFuelPerDay, SHALE_SIM.fracFuelBaseYear) * stat(def, 'fuelMult', 1) / sharing;
  spend(state, fuel, 'fuel', false); w.cost += fuel; j.cost += fuel;
  if (fs) { fs.fracFuel = (fs.fracFuel ?? 0) + fuel; fs.fracCo2T = (fs.fracCo2T ?? 0) + SHALE_SIM.fracCo2TPerDay * stat(def, 'emissionsMult', 1) / sharing; }
  j.fleetCost = (j.fleetCost ?? 0) + serviceDayRate(state, def) / sharing;
  w.cost += serviceDayRate(state, def) / sharing; // fleet is billed on the contract; book its share on the well
  if (j.stagesDone >= j.stages - 1e-6) {
    if (stat(iso, 'needsDrillout', false)) { j.phase = 'drillout'; j.drilloutLeft = Math.ceil(j.stages / stat(eqDef('coiled_tubing_unit'), 'plugsPerDay', COMPLETION.plugDrilloutPerDay)); }
    else toFlowback(state, w);
  }
}
function toFlowback(state, w) { const j = w.frac; j.phase = 'flowback'; j.flowbackLeft = stat(eqDef('flowback_crew'), 'days', SHALE_SIM.flowbackDays); }

function finishJob(state, w) {
  const j = w.frac;
  const L = w.lateral;
  w.frac = null;
  w.problem = null;
  if (j.kind === 'refrac') {
    const p = w.prod;
    const [lo, hi] = stat(eqDef('refrac_job'), 'qiMultRange', [0.5, 0.9]);
    // the restimulated rock is a fresh type curve on top of the old one (spec: refrac adds EUR)
    const add = round((p.ip30 ?? p.qi) * range(state, lo, hi) * SHALE_SIM.refracQiShare, 1);
    (p.rf ??= []).push({ qi: add, age: p.age, day: state.day });
    p.refracs = (p.refracs ?? 0) + 1; p.liftWarned = false;
    p.eurEst = Math.round((p.eurEst ?? 0) + modHypEur(add, p.di, p.b, p.dmin));
    w.status = 'PRODUCING';
    if (j.prevStatus === 'DEPLETED') p.rate = 0;
    notify(state, 'strike', { wellId: w.id, fieldId: w.fieldId, completed: true, text: `${w.name} refracked: +${Math.round(add)} boe/d of new rock.` });
    return;
  }
  L.stages = j.stages; L.proppantLbPerFt = j.design.proppantLbPerFt; L.stageSpacingFt = j.spacingEffFt; L.fluid = j.design.fluid; L.isolation = j.design.isolation;
  L.completedDay = state.day; L.completionCost = Math.round(j.cost); L.child = j.child ?? null;
  if (w.duc && j.startDay - w.duc.since >= 30) state.company.stats.ducsCompleted = (state.company.stats.ducsCompleted ?? 0) + 1;
  w.duc = null;
  state.company.stats.fracStages = (state.company.stats.fracStages ?? 0) + j.stages;
  startShaleProduction(state, w, j.ip30 ?? 0);
  notify(state, 'strike', { wellId: w.id, fieldId: w.fieldId, completed: true, shale: true, stages: j.stages, ip30: j.ip30,
    text: `${w.name} on production after a ${j.stages}-stage frac: IP30 ~${Math.round(j.ip30 ?? 0).toLocaleString('en-US')} boe/d (completion ${fmt$(j.cost)}).` });
}

// DUC carry (tickEcon): $5k/month per drilled-uncompleted well.
export function tickDucs(state) {
  if (!state.shale) return;
  const daily = eraDollars(state, DUC.carryCostPerMonth * 12 / 365.25, DUC.baseYear);
  let n = 0;
  for (const w of Object.values(state.wells)) if (w.duc && (w.status === 'AT_TD' || w.frac?.phase === 'queued')) n++;
  if (n) spend(state, n * daily, 'opex', false);
}
